import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

import { getCountryObjs } from '@luxuryescapes/lib-countries';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import {
  PinnedOffersConfig,
  createPinnedOffersConfig,
  updatePinnedOffersConfig,
} from '~/services/PublicOfferFeedService';

import StandAloneMultiRegionsSelect from '../ForcedRankingsConfig/StandAloneMultiRegionsSelect';

const getCountryOptions = () => {
  const countries = getCountryObjs();
  return Object.values(countries)
    .map((country) => ({
      code: country.iso2,
      label: `${country.countryName} (${country.iso2})`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const countryOptions = getCountryOptions();

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  fetchData: () => void;
  editableRow?: PinnedOffersConfig | null;
};

export default function PinnedOffersConfigForm({ open, setOpen, fetchData, editableRow }: Props) {
  const tenant = useCurrentTenant();
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = !!editableRow;

  const [selectedCountry, setSelectedCountry] = useState<{ code: string; label: string } | null>(null);
  const [distanceLookup, setDistanceLookup] = useState<'10km' | '100km' | '1000km' | 'country_radius'>(
    editableRow?.distance_lookup ?? '10km',
  );
  const [userRegions, setUserRegions] = useState<Array<string>>([]);

  const { handleSubmit } = useForm<Record<string, unknown>>();

  const onCreate = useCallback(async () => {
    if (!selectedCountry) {
      enqueueSnackbar('Please select a country', { variant: 'error' });
      return;
    }

    try {
      const resp = await createPinnedOffersConfig({
        offer_country: selectedCountry.code,
        distance_lookup: distanceLookup,
        user_regions: userRegions,
        brand: tenant.tenant.brand,
      });
      if (resp.status !== 201) {
        enqueueSnackbar('Failed to create pinned offers config', { variant: 'error' });
        return;
      }
      enqueueSnackbar('Pinned offers config created', { variant: 'success' });
      fetchData();
      setOpen(false);
    } catch (e) {
      enqueueSnackbar('Failed to create pinned offers config', { variant: 'error' });
    }
  }, [selectedCountry, enqueueSnackbar, distanceLookup, userRegions, tenant.tenant.brand, fetchData, setOpen]);

  const onUpdate = useCallback(async () => {
    if (!editableRow?.id) {
      enqueueSnackbar('No config ID available for update', { variant: 'error' });
      return;
    }

    if (!selectedCountry) {
      enqueueSnackbar('Please select a country', { variant: 'error' });
      return;
    }

    try {
      const resp = await updatePinnedOffersConfig(
        {
          offer_country: selectedCountry.code,
          user_regions: userRegions,
          distance_lookup: distanceLookup,
          brand: tenant.tenant.brand,
        },
        editableRow?.id,
      );
      if (resp.status !== 200) {
        enqueueSnackbar('Failed to update pinned offers config', { variant: 'error' });
        return;
      }
      enqueueSnackbar('Pinned offers config updated successfully', { variant: 'success' });
      fetchData();
      setOpen(false);
    } catch (e) {
      enqueueSnackbar('Failed to update pinned offers config', { variant: 'error' });
    }
  }, [editableRow?.id, selectedCountry, enqueueSnackbar, userRegions, distanceLookup, fetchData, setOpen]);

  const onSubmit = useMemo(() => (isEditing ? onUpdate : onCreate), [isEditing, onCreate, onUpdate]);

  useEffect(() => {
    if (editableRow) {
      // Find the matching country option for editing
      const countryOption = countryOptions.find((option) => option.code === editableRow.offer_country);
      setSelectedCountry(countryOption || null);
      setDistanceLookup(editableRow.distance_lookup);
      setUserRegions(editableRow.user_regions || []);
    } else {
      // Reset form when creating new
      setSelectedCountry(null);
      setDistanceLookup('10km');
      setUserRegions([]);
    }
  }, [editableRow]);

  const handleClose = () => {
    setOpen(false);
    // Reset form state
    setSelectedCountry(null);
    setDistanceLookup('10km');
    setUserRegions([]);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <DialogTitle>{isEditing ? 'Edit' : 'Create'} Pinned Offers Config</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              options={countryOptions}
              getOptionLabel={(option) => option.label}
              value={selectedCountry}
              onChange={(_, newValue) => setSelectedCountry(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Offer Country"
                  required
                  fullWidth
                  helperText="Select a country from the list"
                />
              )}
              disabled={isEditing}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              renderOption={(props, option) => (
                <li {...props} key={option.code}>
                  {option.label}
                </li>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <StandAloneMultiRegionsSelect
              id="user-regions"
              value={userRegions}
              onChange={setUserRegions}
              label="Website Regions"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel id="distance_lookup_label">Distance Lookup</InputLabel>
              <Select
                id="distance_lookup"
                labelId="distance_lookup_label"
                label="Distance Lookup"
                value={distanceLookup}
                onChange={(e) => setDistanceLookup(e.target.value as typeof distanceLookup)}
              >
                <MenuItem value="10km">10km</MenuItem>
                <MenuItem value="100km">100km</MenuItem>
                <MenuItem value="1000km">1000km</MenuItem>
                <MenuItem value="country_radius">Country Radius</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" type="submit">
          {isEditing ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
